import { isInteger } from "lodash"
import { marginCalc } from "./partUtils"
import {
  CLIENT,
  NOT_SET,
} from "components/Budgeting2023/options/margin/FormulasTab"

export const LIFECYCLE_EMAIL_URL = (
  process.env.REACT_APP_LIFECYCLE_EMAIL_URL || ""
).replace(/\/$/, "")
export const API_URL = process.env.REACT_APP_SERVER_API_URL
export const CORE_URL = process.env.REACT_APP_SERVER_CORE_URL
export const PARTS_CATALOG_URL = process.env.REACT_APP_PARTS_CATALOG_URL

/** Normalize Chargebee portal session from /chargebee/portal (response shape varies). */
export const extractChargebeePortalSession = res => {
  if (!res || typeof res !== "object") return null
  if (res.object === "portal_session") return res
  if (res.portal) return extractChargebeePortalSession(res.portal)
  if (res.portal_session)
    return extractChargebeePortalSession(res.portal_session)
  if (res.id && res.token) return res
  if (res.data && typeof res.data === "object") {
    const nested = extractChargebeePortalSession(res.data)
    if (nested) return nested
  }
  return null
}

export const getCurrency = currency => {
  switch (currency) {
    case "Euro":
    case "EUR":
      return "€"
    case "Dolar":
    case "USD":
      return "$"
    case "Libra":
    case "GBP":
      return "£"
    case "Peso Argentino":
    case "ARS":
      return "ARS$"
    case "Peso Chileno":
    case "CLP":
      return "CLP$"
    case "Peso Colombiano":
    case "COP":
      return "COP$"
    case "Peso Mexicano":
    case "MXN":
      return "MXN$"
    case "Peso Uruguayo":
    case "UYU":
      return "UYU$"
    case "Peso Hondureño":
    case "HNL":
      return "HNL"
    case "Balboa":
    case "PAB":
      return "B/."
    default:
      return "€"
  }
}

export const formatTableValue = value => {
  if (value === "0.00" || value === "0,00") {
    return 0
  }

  if (typeof value !== "number") {
    return value
  }

  if (value === 0) {
    return 0
  }

  if (isInteger(value)) {
    return value
  }

  const roundedNumber = value.toFixed(2)
  return Number.isInteger(roundedNumber)
    ? parseInt(roundedNumber)
    : parseFloat(roundedNumber)
}

/**
 * Calcula el precio final para el cliente a partir del precio unitario,
 * la cantidad y el margen de beneficio. Si el margen no está definido,
 * se calcula automáticamente usando la función `marginCalc`.
 *
 * @param {Object} p - Objeto que representa una partida.
 * @param {number} p.price - Precio unitario de la partida.
 * @param {number} p.quantity - Cantidad de unidades.
 * @param {number} [p.margin] - Margen de beneficio en porcentaje (opcional).
 * @returns {number} Precio final calculado para el cliente.
 */
export const partidaGetClientPrice = (p, formula) => {
  const margin = p?.margin != null ? p.margin : marginCalc(p, formula)

  if (margin === 100) {
    return p.price * p.quantity * 100
  }

  const divisor = 1 - margin / 100

  if (divisor === 0) return 0

  return (p.price * p.quantity) / divisor
}

export const calcularMargenPrice = (partida, formula) => {
  if (formula === CLIENT || formula === NOT_SET) {
    const margin =
      partida?.margin != null ? partida.margin : marginCalc(partida)

    const divisor = 1 - margin / 100
    if (divisor === 0) return 0

    const clientP =
      margin === 100
        ? partida.price * partida.quantity * 100
        : (partida.price * partida.quantity) / divisor

    //partidaGetClientPrice(partida)
    const x = partida.price * partida.quantity
    const result = clientP - x

    return parseFloat(result).toFixed(2)
  } else {
    // formula === COST
    const clientP = partidaGetClientPrice(partida)
    const x = partida.price * partida.quantity
    const result = clientP - x

    return parseFloat(result).toFixed(2)
  }
}

export const emptyMeasure = measure => {
  return (
    (Object.hasOwn(measure, "description")
      ? measure.description == ""
      : true) &&
    (Object.hasOwn(measure, "quantity") ? measure.quantity == 0 : true) &&
    (Object.hasOwn(measure, "depth")
      ? measure.depth == 0 || measure.depth == 1
      : true) &&
    (Object.hasOwn(measure, "width") ? measure.width == 0 : true) &&
    (Object.hasOwn(measure, "height") ? measure.height == 0 : true) &&
    (Object.hasOwn(measure, "total") ? measure.total == 0 : true)
  )
}

export const checkIfMeasuresAreEmpty = measures => {
  for (let i = 0; i < measures.length; i++) {
    if (!emptyMeasure(measures[i])) return false
  }
  return true
}

/**
 * Calculates the amount of a decomposition based on the given detail values.
 *
 * @param {Object} detail - Object containing the calculation details.
 * @param {string|number} detail.type - Type of calculation. If `"coste"` or `0`, it is calculated as a percentage.
 * @param {number|string} detail.price - Unit price or total price per unit.
 * @param {number|string} detail.priceReduced - Unit price reduced or total price per unit.
 * @param {number|string} detail.quantity - Quantity or percentage, depending on the type.
 * @param {number|string} detail.waste - Waste quantity.
 * @param {number|string} [detail.total] - Pre-calculated total (optional).
 *
 * @returns {string} The calculated amount as a string with 2 decimal places.
 */
export const calcularImporteDeDescomposicion = detail => {
  const { price, priceReduced, factor, quantity, waste, total, unity } = detail

  let priceNumber =
    priceReduced !== undefined && priceReduced !== null && priceReduced !== ""
      ? Number(priceReduced)
      : Number(price)
  priceNumber = priceNumber || 0

  let quantityNumber = Number(quantity) || 0

  let wasteNumber = 0

  if (
    waste === undefined ||
    (waste === null && factor !== undefined && factor !== null)
  ) {
    wasteNumber = quantityNumber * (Number(factor) - 1)
  } else {
    wasteNumber = Number(waste)
  }

  if (unity === "%") {
    quantityNumber = quantityNumber > 1 ? quantityNumber / 100 : quantityNumber
  }

  if (total !== undefined && total !== null) return parseFloat(total)

  return parseFloat(priceNumber * (quantityNumber + wasteNumber))
}

const AUTH_PAGES = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/part-browser",
]

export const isOnAuthPage = () =>
  AUTH_PAGES.some(page => window.location.pathname.startsWith(page))
